/**
 * CompanyConfig — the canonical schema every company configuration must follow.
 *
 * Keep this file as the single source of truth for the shape of a company config.
 * When adding fields, update ALL files in `./companies/` so they stay in sync.
 *
 *   @typedef {Object} ContactInfo
 *   @property {string} email      Primary contact / support email
 *   @property {string} phone      E.164 or display-friendly phone number
 *   @property {string} address    Multi-line address (use \n for line breaks)
 *
 *   @typedef {Object} SocialLinks
 *   @property {string} [linkedin]
 *   @property {string} [twitter]
 *   @property {string} [facebook]
 *   @property {string} [instagram]
 *   @property {string} [youtube]
 *
 *   @typedef {Object} HeroContent
 *   @property {string} title
 *   @property {string} description
 *   @property {string} primaryCta       Label of the primary CTA button
 *   @property {string} [secondaryCta]   Optional second CTA
 *
 *   @typedef {Object} Service
 *   @property {string} title
 *   @property {string} description
 *   @property {string} [icon]          Lucide / Phosphor icon name
 *
 *   @typedef {Object} Testimonial
 *   @property {string} name
 *   @property {string} role
 *   @property {string} quote
 *
 *   @typedef {Object} PortfolioItem
 *   @property {string} title
 *   @property {string} summary
 *   @property {string} [image]
 *
 *   @typedef {Object} AboutContent
 *   @property {string} headline
 *   @property {string} body
 *   @property {string[]} [bullets]
 *
 *   @typedef {Object} SeoConfig
 *   @property {string} title
 *   @property {string} description
 *   @property {string} [ogImage]
 *
 *   @typedef {Object} BrandConfig
 *   @property {string} primaryColor      Hex, e.g. "#FF5500"
 *   @property {string} secondaryColor    Hex, e.g. "#1F4E8C"
 *   @property {string} [accentColor]
 *   @property {string} logo              Imported logo asset (default)
 *   @property {string} [logoDark]        Optional alt for dark backgrounds
 *   @property {string} favicon           Imported favicon asset
 *
 *   @typedef {Object} CompanyConfig
 *   @property {string} slug              Unique id, used for env var + domain match
 *   @property {string} name              Full legal / display name  — "Global Technical Engineering Services"
 *   @property {string} shortName         Short brand mark  — "GTES"
 *   @property {string} tagline           One-line descriptor shown next to the brand
 *   @property {string} website           Public website URL (https://...)
 *   @property {string[]} domains         Hostnames that map to this company (for domain-based auto-select)
 *   @property {BrandConfig} brand
 *   @property {ContactInfo} contact
 *   @property {SocialLinks} social
 *   @property {HeroContent} hero
 *   @property {AboutContent} about
 *   @property {Service[]} services
 *   @property {Testimonial[]} testimonials
 *   @property {PortfolioItem[]} portfolio
 *   @property {SeoConfig} seo
 *   @property {Object} [extras]          Free-form space for anything else per-company
 */

export const REQUIRED_KEYS = [
  "slug", "name", "shortName", "tagline", "website", "domains",
  "brand", "contact", "social", "hero", "about",
  "services", "testimonials", "portfolio", "seo",
];

/** Minimal runtime guard — throws if a config is missing a required top-level key. */
export function assertCompanyShape(cfg, source = "unknown") {
  if (!cfg || typeof cfg !== "object") {
    throw new Error(`[CompanyConfig] ${source}: config must be an object`);
  }
  const missing = REQUIRED_KEYS.filter((k) => !(k in cfg));
  if (missing.length) {
    throw new Error(`[CompanyConfig] ${source}: missing keys → ${missing.join(", ")}`);
  }
  const b = cfg.brand || {};
  for (const k of ["primaryColor", "secondaryColor", "logo", "favicon"]) {
    if (!b[k]) throw new Error(`[CompanyConfig] ${source}.brand.${k} is required`);
  }
  return cfg;
}
